// One serialized writer per browser. Revisions reject other devices' stale writes.
export class SaveQueue {
  constructor(send, storage, changed = () => {}) {
    this.send = send; this.storage = storage; this.changed = changed;
    this.pending = new Map(); this.revisions = new Map(); this.blocked = new Map();
    this.running = null;
    this.owner = crypto.randomUUID();
    this.recoveryKeys = new Map();
    this.recovered = false;
  }
  key(id) { return `kdm_outbox__${this.owner}__${id}`; }
  remember(c) { this.revisions.set(c.id, c.revision || 0); }
  enqueue(c) {
    const copy = { ...c, mutationId: crypto.randomUUID() };
    this.pending.set(c.id, copy);
    // The outbox is recovery only; the complete campaign is saved on the server.
    this.storage.setItem(this.key(c.id), JSON.stringify(copy));
    this.changed();
  }
  async flush() {
    if (this.running) { await this.running; return this.flush(); }
    this.running = this.drain();
    try { await this.running; } finally { this.running = null; }
  }
  async drain() {
    for (const [id, snapshot] of this.pending) {
      if (this.blocked.has(id)) throw this.blocked.get(id);
      const payload = { ...snapshot, revision: this.revisions.get(id) ?? snapshot.revision ?? 0 };
      // Journal the exact request, including its idempotency token and revision.
      this.storage.setItem(this.key(id), JSON.stringify(payload));
      try {
        const result = await this.send(payload);
        this.revisions.set(id, result.revision);
        if (this.pending.get(id) === snapshot) {
          this.pending.delete(id);
          const journal = JSON.parse(this.storage.getItem(this.key(id)) || 'null');
          if (journal?.mutationId === payload.mutationId) this.storage.removeItem(this.key(id));
        } else {
          const newer = { ...this.pending.get(id), revision: result.revision };
          this.pending.set(id, newer);
          this.storage.setItem(this.key(id), JSON.stringify(newer));
        }
        for (const key of this.recoveryKeys.get(id) || []) {
          const old = JSON.parse(this.storage.getItem(key) || 'null');
          if (old?.mutationId === payload.mutationId) this.storage.removeItem(key);
        }
        this.changed(result, id);
      } catch (e) {
        if (e.status === 409 || e.status === 422) this.blocked.set(id, e);
        throw e;
      }
    }
    // Map iteration doesn't revisit a replaced entry.
    if (this.pending.size) await this.drain();
  }
  recover() {
    if (this.recovered) return;
    this.recovered = true;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (!key?.startsWith('kdm_outbox__')) continue;
      const c = JSON.parse(this.storage.getItem(key));
      if (c?.id) {
        if (this.pending.has(c.id) && this.pending.get(c.id).mutationId !== c.mutationId) {
          throw Error('Multiple unsaved versions exist. Download the recovery copies before reconciling them.');
        }
        this.pending.set(c.id, c); this.remember(c);
        this.recoveryKeys.set(c.id, [...(this.recoveryKeys.get(c.id) || []), key]);
      }
    }
  }
}
